/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sentences } from '../lib/text';
import {
  Megaphone, AlertTriangle, Calendar, ExternalLink, ShieldAlert,
  FileText, Beaker, ChevronRight, Skull, Eye, Atom, Sparkles, Rocket,
  UserCheck, Radio, FileSearch, Video, Music, Image as ImageIcon,
} from 'lucide-react';

const ICON_MAP = {
  atom: Atom, sparkles: Sparkles, rocket: Rocket,
  'user-check': UserCheck, radio: Radio, 'file-search': FileSearch,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DisclosuresPage() {
  const [disclosures, setDisclosures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/disclosures?limit=50`);
        setDisclosures(data.disclosures || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const biolab = disclosures.find(d => d.slug === 'bio-labs-gabbard');
  const tranche4 = disclosures.find(d => d.release_id === 'PURSUE-04');
  const others = disclosures.filter(d => d.slug !== 'bio-labs-gabbard' && d.release_id !== 'PURSUE-04');

  return (
    <div data-testid="disclosures-page">
      {/* HERO */}
      <section className="relative border-b border-amber-500/20 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="absolute inset-0" style={{
          background:
            'radial-gradient(circle at 18% 20%, rgba(245,158,11,0.10), transparent 50%), radial-gradient(circle at 80% 80%, rgba(220,38,38,0.05), transparent 50%)',
        }} />
        <div className="relative mx-auto max-w-[1400px] px-6 py-14">
          <div className="flex items-center gap-3 mono text-[11px] tracking-[0.3em] text-amber-300/90 uppercase mb-4">
            <span className="cia-stamp"><Megaphone className="h-3 w-3" /> Divulgations Gouvernementales</span>
            <span className="text-amber-500/40">::</span>
            <span>Déclarations officielles · Hors-périmètre UAP</span>
          </div>
          <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            <span className="text-1">Divulgations</span>{' '}
            <span className="italic text-amber-300">Gouvernementales</span>
          </h1>
          <div className="text-2 text-base mt-4 max-w-3xl leading-relaxed">
            <Sentences text="Cette section rassemble les déclarations officielles d'institutions américaines (DNI, départements fédéraux) sur des sujets sensibles autres que les PAN. Les contenus présentés ici sont des reproductions de déclarations publiques. Ils ne constituent pas des documents déclassifiés vérifiés par PURSUE FR." />
          </div>
        </div>
      </section>

      {/* ARTICLE VEDETTE — Tranche 4 UAP */}
      {tranche4 && (
        <article className="border-b border-white/5">
          <div className="mx-auto max-w-[1200px] px-6 py-12">
            {/* Cover image */}
            {tranche4.cover_image && (
              <div className="relative rounded-lg overflow-hidden mb-8 border border-amber-500/20">
                <img
                  src={`${BACKEND_URL}${tranche4.cover_image}`}
                  alt="Couverture Tranche 4"
                  className="w-full max-h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-6 right-6">
                  <span className="mono text-[10px] tracking-widest px-2 py-1 rounded bg-black/70 border border-amber-500/40 text-amber-200 uppercase">
                    TRANCHE 04 · 10 juillet 2026 · Programme PURSUE
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <span className="declass-mini">DÉCLASSIFIÉ · PUBLIC</span>
              <span className="mono text-[10px] tracking-widest text-3 uppercase inline-flex items-center gap-1.5">
                <Calendar className="h-3 w-3" /> 10 juillet 2026
              </span>
              <span className="mono text-[10px] tracking-widest text-amber-300 uppercase inline-flex items-center gap-1.5">
                <ShieldAlert className="h-3 w-3" /> Département de la Guerre · Maison Blanche
              </span>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-1 mb-3"
            >
              Tranche 4 — <span className="italic text-amber-300">Programme PURSUE</span> :
              40 dossiers déclassifiés
            </motion.h2>
            <p className="text-3 text-sm mono mb-8 italic">
              « Focus : Rapport UAP non résolu · Golfe d'Amérique · 2019.
              Six révélations majeures publiées par le Département de la Guerre. »
            </p>

            {/* Répartition des 40 fichiers */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
              <KeyStat icon={FileText} label="Rapports" value="14" tint="amber" />
              <KeyStat icon={Video} label="Vidéos" value="19" tint="cyan" />
              <KeyStat icon={Music} label="Audios" value="4" tint="emerald" />
              <KeyStat icon={ImageIcon} label="Images" value="3" tint="rose" />
            </div>

            {/* Contexte */}
            <div className="prose prose-invert max-w-none space-y-4 text-2 leading-relaxed mb-10">
              <p>
                Le 10 juillet 2026, la <strong className="text-1">Maison Blanche</strong> a confirmé
                la publication de la 4ème tranche de documents déclassifiés du programme
                <strong className="text-amber-300"> PURSUE</strong> (Program for Unidentified
                Anomalous Phenomena — Research, Study & Evaluation). Cette livraison de{' '}
                <strong className="text-1">40 fichiers</strong> constitue à ce jour la publication
                la plus riche en contenu multimédia, avec près de la moitié des dossiers sous forme
                audiovisuelle.
              </p>
              <p>
                Six révélations majeures sont mises en avant par le Département de la Guerre. Toutes
                concernent des <strong className="text-amber-300">cas non résolus</strong>, corroborés
                par plusieurs capteurs indépendants (radar, infrarouge, optique, HUD embarqué) et
                témoignages sous serment d'aviateurs vétérans.
              </p>
            </div>

            {/* Les 6 révélations */}
            <h3 className="serif text-1 text-2xl md:text-3xl mb-5">Les 6 révélations majeures</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {(tranche4.bombshells || []).map((b, i) => {
                const Icon = ICON_MAP[b.icon] || FileText;
                return (
                  <motion.div
                    key={b.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="glass rounded-lg p-5 border-l-4 border-amber-500/60"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="h-10 w-10 rounded bg-amber-400/10 border border-amber-300/30 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-amber-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="mono text-[9px] tracking-widest text-amber-300/80 uppercase mb-1">
                          Révélation {i + 1}
                        </div>
                        <div className="text-1 text-base font-medium leading-tight">
                          {b.title}
                        </div>
                        <div className="mono text-[10px] text-3 mt-1">
                          {b.location}
                        </div>
                      </div>
                    </div>
                    <p className="text-2 text-sm leading-relaxed mb-2">
                      {b.summary}
                    </p>
                    <p className="text-3 text-xs leading-relaxed pt-2 border-t border-white/5">
                      {b.details}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            {/* Sources */}
            <div className="mt-8 p-4 rounded-lg border border-cyan-500/25 bg-cyan-500/5">
              <div className="mono text-[10px] tracking-widest text-cyan-300 uppercase mb-3 inline-flex items-center gap-1.5">
                <ExternalLink className="h-3 w-3" /> Sources officielles
              </div>
              <div className="space-y-1">
                {(tranche4.sources || []).map(s => (
                  <a key={s} href={s} target="_blank" rel="noreferrer"
                     className="block mono text-xs text-cyan-300 hover:text-cyan-200">
                    → {s}
                  </a>
                ))}
                <Link to="/archives?agency=DOW" className="block mono text-xs text-amber-300 hover:text-amber-200 mt-2 pt-2 border-t border-white/5">
                  → Consulter les 40 dossiers dans l'archive française
                </Link>
              </div>
            </div>
          </div>
        </article>
      )}

      {/* ARTICLE PRINCIPAL — Bio-labs Gabbard */}
      {biolab && (
        <article className="border-b border-white/5">
          <div className="mx-auto max-w-[1100px] px-6 py-12">
            <div className="flex items-center gap-3 mb-5">
              <span className="declass-mini">DÉCLARATION OFFICIELLE</span>
              <span className="mono text-[10px] tracking-widest text-3 uppercase inline-flex items-center gap-1.5">
                <Calendar className="h-3 w-3" /> 12 juin 2026
              </span>
              <span className="mono text-[10px] tracking-widest text-rose-300 uppercase inline-flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3" /> Source : DNI · États-Unis
              </span>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-1 mb-3"
            >
              Divulgation Bio-labs : la DNI{' '}
              <span className="italic text-amber-300">Tulsi Gabbard</span>{' '}
              déclassifie le programme secret
            </motion.h2>
            <p className="text-3 text-sm mono mb-8 italic">
              « 120+ laboratoires dans 30 pays — financement administrations Obama-Biden — recherches dites de
              gain de fonction confirmées. »
            </p>

            {/* Encadré clé */}
            <div className="grid md:grid-cols-3 gap-3 mb-10">
              <KeyStat icon={Beaker} label="Laboratoires identifiés" value="120+" tint="amber" />
              <KeyStat icon={Eye} label="Pays concernés" value="30" tint="cyan" />
              <KeyStat icon={Skull} label="En Ukraine" value="40+" tint="rose" />
            </div>

            <div className="prose prose-invert max-w-none space-y-5 text-2 leading-relaxed">
              <p>
                Le 12 juin 2026, la <strong className="text-1">Directrice du Renseignement National</strong> des
                États-Unis, <strong className="text-amber-300">Tulsi Gabbard</strong>, a procédé à une
                déclassification officielle révélant l'existence d'un vaste programme de bio-laboratoires
                financés à l'étranger par les administrations Obama puis Biden.
              </p>

              <h3 className="serif text-1 text-2xl mt-8 mb-2">Identification du programme</h3>
              <p>
                Selon la déclaration de la DNI, <strong className="text-1">plus de 120 bio-laboratoires
                financés par les États-Unis</strong> ont été identifiés dans une trentaine de pays. Plus
                de <strong className="text-1">40 de ces installations sont situées en Ukraine</strong>,
                officiellement présentées sous l'égide de coopérations sanitaires bilatérales, mais dont
                la véritable nature serait, selon la DNI, restée occultée du grand public.
              </p>

              <h3 className="serif text-1 text-2xl mt-8 mb-2">Recherches de type « gain de fonction »</h3>
              <p>
                La déclaration confirme que certaines de ces installations ont mené des programmes
                qualifiés de <strong className="text-amber-300">« gain de fonction »</strong> (gain
                of function) — des protocoles consistant à modifier expérimentalement des agents
                pathogènes pour en renforcer la transmissibilité ou la virulence, dans un objectif
                de recherche défensif allégué. Tulsi Gabbard indique que la nature exacte de ces
                pathogènes a été dissimulée au public américain et aux populations locales des
                pays-hôtes.
              </p>

              <h3 className="serif text-1 text-2xl mt-8 mb-2">Lanceurs d'alerte réduits au silence</h3>
              <p>
                D'après la DNI, plusieurs <strong className="text-1">lanceurs d'alerte</strong>
                {' '}au sein du programme — personnels scientifiques et administratifs — auraient été
                soumis à des pressions, des menaces de poursuites pénales et des clauses de
                confidentialité étendues, afin d'empêcher toute prise de parole publique.
              </p>

              <h3 className="serif text-1 text-2xl mt-8 mb-2">Implications</h3>
              <p>
                La déclassification orchestrée par la DNI ouvre la voie à de potentielles auditions
                publiques au Congrès des États-Unis et à des demandes de transparence sur l'ensemble
                des programmes biologiques financés à l'étranger sur les vingt dernières années.
                Selon la DNI, des documents complémentaires seront publiés progressivement dans les
                semaines à venir.
              </p>
            </div>

            {/* Disclaimer */}
            <div className="mt-10 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
              <div className="mono text-[10px] tracking-widest text-amber-300 uppercase inline-flex items-center gap-1.5 mb-2">
                <AlertTriangle className="h-3 w-3" /> Avertissement éditorial
              </div>
              <p className="text-3 text-xs leading-relaxed">
                Cet article reproduit une déclaration publique attribuée à la DNI Tulsi Gabbard.
                Les chiffres et affirmations cités proviennent de cette déclaration et n'ont pas
                été indépendamment vérifiés par PURSUE FR. Le site se borne à les rapporter en
                tant que <em>position officielle annoncée</em>, distincte des documents déclassifiés
                figurant dans l'Atlas UAP. Les lecteurs sont invités à consulter les sources primaires
                officielles pour leur propre évaluation.
              </p>
            </div>
          </div>
        </article>
      )}

      {/* AUTRES DIVULGATIONS — UAP releases */}
      <section className="border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 py-12">
          <h2 className="serif text-2xl md:text-3xl text-1 mb-2">Autres divulgations récentes</h2>
          <p className="text-3 text-sm mb-6">
            Tranches officielles du programme PURSUE et alertes détectées par l'archive automatique.
          </p>
          {loading ? (
            <p className="text-3 mono text-sm">Chargement…</p>
          ) : others.length === 0 ? (
            <p className="text-3 mono text-sm">Aucune autre divulgation pour le moment.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {others.map((d, i) => (
                <motion.div
                  key={d.detected_at + i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="glass rounded-lg overflow-hidden border-l-2 border-amber-500/60"
                >
                  {d.cover_image && (
                    <div className="relative aspect-video overflow-hidden bg-black/40">
                      <img
                        src={`${BACKEND_URL}${d.cover_image}`}
                        alt=""
                        className="w-full h-full object-cover opacity-80"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      {d.release_id && (
                        <span className="absolute top-2 left-2 mono text-[10px] tracking-widest px-2 py-1 rounded bg-black/60 text-amber-200 uppercase">
                          {d.release_id.replace('PURSUE-0','TRANCHE 0')}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    <div className="mono text-[10px] tracking-widest text-amber-300/90 uppercase mb-2 inline-flex items-center gap-1.5">
                      <FileText className="h-3 w-3" />
                      {new Date(d.detected_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    <div className="text-1 text-sm font-medium leading-snug mb-2">
                      {d.title || `${d.new_docs_count} nouveaux dossiers détectés`}
                    </div>
                    {d.description && (
                      <p className="text-3 text-xs leading-relaxed mb-3 line-clamp-4">{d.description}</p>
                    )}
                    {d.keywords_matched && d.keywords_matched.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {d.keywords_matched.slice(0, 5).map(k => (
                          <span key={k} className="mono text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-2 uppercase tracking-wide">{k}</span>
                        ))}
                      </div>
                    )}
                    {d.sources && d.sources.length > 0 && (
                      <div className="space-y-1 mt-2 border-t border-white/5 pt-2">
                        {d.sources.map(s => (
                          <a key={s} href={s} target="_blank" rel="noreferrer" className="block mono text-[10px] text-cyan-300 hover:text-cyan-200 truncate">
                            <ExternalLink className="inline h-3 w-3 mr-1" />{s}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                      <Link to="/archives" className="text-amber-300 text-[11px] inline-flex items-center gap-1 mono">
                        Voir les archives <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function KeyStat({ icon: Icon, label, value, tint = 'amber' }) {
  const colors = {
    amber: 'text-amber-300 border-amber-500/40',
    cyan: 'text-cyan-300 border-cyan-500/40',
    rose: 'text-rose-300 border-rose-500/40',
  };
  return (
    <div className={`glass rounded-md p-4 border-l-4 ${colors[tint]}`}>
      <div className="mono text-[9px] tracking-widest text-3 uppercase inline-flex items-center gap-1.5 mb-2">
        <Icon className={`h-3 w-3 ${colors[tint]}`} /> {label}
      </div>
      <div className={`serif text-4xl ${colors[tint]}`}>{value}</div>
    </div>
  );
}
